import React from 'react';

import { useIntl } from 'react-intl';

import FeedbackIcon from '@atlaskit/icon/core/feedback';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { token } from '@atlaskit/tokens';
import type { ExperimentConfig } from '@atlassian/search-experiment/types';

import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import type { ApplicationModes } from '../../constants/application-modes';
import type { PrimaryProductKey, ProductKeys } from '../../constants/product-keys';
import type { UserDetailsWithPicture, UserDetailsWithStatus } from '../../constants/types';
import { onRightPanelFeedbackCancelClicked } from '../../utils/events/on-right-panel-feedback-cancel-clicked';
import { onRightPanelFeedbackClicked } from '../../utils/events/on-right-panel-feedback-clicked';
import { onRightPanelFeedbackSubmitClicked } from '../../utils/events/on-right-panel-feedback-submit-clicked';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../utils/experiments';
import { getUser } from '../../utils/get-user';
import FeedbackComponent from '../feedback';

import { messages } from './messages';

const dividerStyles = xcss({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	height: '1px',
	marginBlock: 'space.075',
	marginInline: 'space.150',
});

const wrapperStyles = xcss({
	alignItems: 'center',
	display: 'flex',
	font: 'font.body',
	gap: 'space.075',
	marginTop: 'space.100',
	paddingBlock: 'space.050',
	paddingLeft: 'space.150',
});

const feedbackIconContainer = xcss({
	padding: 'space.050',
	justifyContent: 'center',
});

export type GiveFeedbackProps = {
	cloudId?: string;
	primaryProduct?: PrimaryProductKey;
	experimentConfig: ExperimentConfig;
	selectedProducts: ProductKeys[];
	user?: UserDetailsWithPicture | UserDetailsWithStatus;
	appMode: ApplicationModes;
	requestId?: string;
	traceId?: string;
	isCQL?: boolean;
};

export const GiveFeedback = ({
	cloudId,
	primaryProduct,
	experimentConfig,
	selectedProducts,
	user,
	appMode,
	requestId,
	traceId,
	isCQL,
}: GiveFeedbackProps): React.JSX.Element => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const { formatMessage } = useIntl();
	return (
		<UFOSegment name="search-page-feedback">
			{getUnifiedSearchRightRailEnhancementsExpValue() !== 'single-select' && (
				<Box xcss={dividerStyles} />
			)}
			<Box xcss={[wrapperStyles]}>
				<Box xcss={feedbackIconContainer}>
					<FeedbackIcon color="currentColor" label="Give feedback" />
				</Box>
				<Text>{formatMessage(messages.improveSearch)}</Text>
				<FeedbackComponent
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					experimentConfig={experimentConfig}
					selectedProducts={selectedProducts}
					user={getUser(user)}
					applicationMode={appMode}
					source="search-page"
					appearance="link"
					requestId={requestId || ''}
					traceId={traceId || ''}
					isCQL={isCQL}
					onFeedbackClick={() => {
						fireAnalyticsEvent(
							onRightPanelFeedbackClicked({ commonAttributes, nonPrivacySafeAttributes }),
						);
					}}
					onFeedbackSubmit={() => {
						fireAnalyticsEvent(
							onRightPanelFeedbackSubmitClicked({ commonAttributes, nonPrivacySafeAttributes }),
						);
					}}
					onFeedbackCancel={() => {
						fireAnalyticsEvent(
							onRightPanelFeedbackCancelClicked({ commonAttributes, nonPrivacySafeAttributes }),
						);
					}}
				/>
			</Box>
		</UFOSegment>
	);
};
